import { useEffect, useRef } from "react";

/*
 * Android's Back button (and the browser's) walks back through the app: it closes the open dialog,
 * then leaves the sub-page, then returns to the tables. Screens and dialogs are React state, not
 * URLs, so without this the browser had nothing to go back to inside the app and left it instead —
 * which an installed app shows as a blank page.
 *
 * One extra history entry (the "guard") always sits on top of the app's own entry. Back pops it;
 * the innermost registered handler runs and the guard is pushed again. With nothing left to close,
 * the installed app stays put rather than going blank. A browser tab lets Back leave the page.
 */

/** How deep a handler sits: a dialog closes before its sub-page, a sub-page before its screen. */
export type BackLevel = "screen" | "page" | "dialog";

const LEVEL_ORDER: Record<BackLevel, number> = { screen: 0, page: 1, dialog: 2 };
const GUARD_KEY = "easyordersBackGuard";

type Entry = { id: number; level: BackLevel; handler: () => void };

const entries: Entry[] = [];
let nextId = 1;
let installed = false;

function isStandalone(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function guardIsCurrent(): boolean {
  return Boolean((window.history.state as Record<string, unknown> | null)?.[GUARD_KEY]);
}

function pushGuard(): void {
  if (!guardIsCurrent()) {
    window.history.pushState({ ...(window.history.state ?? {}), [GUARD_KEY]: true }, "");
  }
}

/** The innermost handler: the highest level wins, and the latest registration within a level. */
function innermost(): Entry | undefined {
  return entries.reduce<Entry | undefined>((best, entry) =>
    !best || LEVEL_ORDER[entry.level] >= LEVEL_ORDER[best.level] ? entry : best, undefined);
}

function onPopState(): void {
  const entry = innermost();
  if (entry) {
    pushGuard();
    entry.handler();
    return;
  }
  // Nothing to close. The installed app has nowhere sensible to go back to, so it stays here.
  if (isStandalone()) {
    pushGuard();
  }
}

/** Call once the app knows it is staying on this origin (see preferPrimaryOrigin). */
export function installBackNavigation(): void {
  if (installed || typeof window === "undefined") {
    return;
  }
  installed = true;
  window.addEventListener("popstate", onPopState);
  if (entries.length || isStandalone()) {
    pushGuard();
  }
}

function register(level: BackLevel, handler: () => void): () => void {
  const entry: Entry = { id: nextId++, level, handler };
  entries.push(entry);
  if (installed) {
    pushGuard();
  }
  return () => {
    const index = entries.findIndex((item) => item.id === entry.id);
    if (index >= 0) {
      entries.splice(index, 1);
    }
  };
}

/** While `active`, the device Back button calls `onBack` instead of leaving the screen. */
export function useBackHandler(active: boolean, onBack: () => void, level: BackLevel = "dialog"): void {
  const latest = useRef(onBack);
  latest.current = onBack;
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    return register(level, () => latest.current());
  }, [active, level]);
}
